import { useMemo } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';

import { EventGrid } from '@/components/common/EventGrid';
import { HeadingLevel } from '@/components/common/MarkUp';
import { TabView } from '@/components/common/TabView';
import { Layout } from '@/components/layout/Layout';
import { Section } from '@/components/layout/Section';
import { ListingsHero } from '@/components/page/blog/ListingsHero';
import { BASE_REVALIDATE } from '@/lib/constants';
import { dateInFuture, dateInPast } from '@/lib/dates';
import {
  allEventsQuery,
  eventSettingsQuery,
  localeTranslationsQuery,
  navigationQuery,
  pageQuery,
  siteSettingsQuery,
} from '@/lib/queries';
import { getClient, readToken } from '@/lib/sanityUtils';
import { useDynamicYield } from '@/lib/useDynamicYield';
import { getValidLocale } from '@/lib/utils';

import type {
  SanityEventSettingsType,
  SanityNavigationType,
  SanityPageType,
  SanitySiteSettingsType,
} from '@/types/sanity';
import type { Locale, Translation } from '@/types/shared';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';

const slug = 'events';

export const getStaticProps = (async ({ locale, draftMode = false }) => {
  const client = getClient(draftMode ? { token: readToken } : undefined);

  const validLocale = getValidLocale(locale);

  if (!validLocale) {
    return {
      notFound: true,
    };
  }

  const location = validLocale === 'en-US' ? 'US' : 'UK';

  const eventsPage = await client.fetch<SanityPageType>(
    pageQuery({ locale: validLocale, slug }),
  );

  const translations = await client.fetch<Translation[] | null>(
    localeTranslationsQuery(eventsPage),
  );

  const eventSettings =
    await client.fetch<SanityEventSettingsType>(eventSettingsQuery());

  const siteSettings = await client.fetch<SanitySiteSettingsType>(
    siteSettingsQuery({ locale: validLocale }),
  );

  const navigation = await client.fetch<SanityNavigationType>(
    navigationQuery({ locale: validLocale }),
  );

  const events = await client.fetch(allEventsQuery({ location }));

  return {
    props: {
      eventSettings,
      eventsPage,
      events,
      siteSettings,
      locale: validLocale,
      draftMode,
      navigation,
      location,
      token: draftMode ? readToken : '',
      translations,
    },
    revalidate: Number(process.env.REVALIDATE || BASE_REVALIDATE),
  };
}) satisfies GetStaticProps;

const EventsPage = ({
  eventSettings,
  siteSettings,
  events: initialEvents,
  eventsPage,
  locale,
  draftMode,
  navigation,
  translations,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const router = useRouter();

  const upcomingEvents = useMemo(() => {
    return initialEvents.filter((event) =>
      dateInFuture(new Date(event.endsAtDate)),
    );
  }, [initialEvents]);

  const onDemandEvents = useMemo(() => {
    return initialEvents
      .filter(
        (event) => dateInPast(new Date(event.endsAtDate)) && event.onDemand,
      )
      .sort(
        (a, b) =>
          b.startsAtDate.localeCompare(a.startsAtDate) ||
          a.startsAtTime.localeCompare(b.startsAtTime),
      );
  }, [initialEvents]);

  const { recommendationContext } = useDynamicYield(locale, 'OTHER');

  let tabs = [];

  if (upcomingEvents.length === 0) {
    tabs = [{ label: 'On Demand', events: onDemandEvents }];
  } else if (onDemandEvents === 0) {
    tabs = [{ label: 'Upcoming', events: upcomingEvents }];
  } else {
    tabs = [
      { label: 'Upcoming', events: upcomingEvents },
      { label: 'On Demand', events: onDemandEvents },
    ];
  }

  const tabData = tabs.map((tab) => {
    return {
      label: tab.label,
      variant: 'standard',
      content: (
        <EventGrid
          events={tab.events}
          locale={router?.locale as Locale}
          fallbackImage={eventSettings?.featuredImageDefault}
        />
      ),
    };
  });

  return (
    <Layout
      SEO={eventsPage?.SEO}
      localeTranslations={translations}
      siteSettings={siteSettings}
      preview={draftMode}
      navigation={navigation}
      recommendationContext={recommendationContext}
    >
      <ListingsHero heading={eventSettings?.heading} />

      <Section backgroundColour="--background-subtle" verticalPadding>
        <HeadingLevel>
          <StyledEventsTabView tabs={tabData} />
        </HeadingLevel>
      </Section>
    </Layout>
  );
};

const StyledEventsTabView = styled(TabView)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export default EventsPage;
